import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronRight, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { GlassButton } from './ui/glass-button';
import logoImg from '../assets/logo.png';
import ApplicationModal from './ApplicationModal';

type NavLink = {
  name: string;
  href?: string;
  subLinks?: { name: string; href: string; }[];
};

const navLinks: NavLink[] = [
  { name: 'ANA SAYFA', href: '/' },
  { name: 'HAKKIMIZDA', href: '/hakkimizda' },
  { 
    name: 'KULÜP', 
    subLinks: [
      { name: 'YÖNETİM KURULUMUZ', href: '/yonetim-kurulumuz' },
      { name: 'TÜZÜĞÜMÜZ', href: '/tuzugumuz' },
      { name: 'YARIŞMA TAKVİMİ', href: '/yarisma-takvimi' },
    ]
  },
  { name: 'NASIL KATILABİLİRSİNİZ?', href: '/nasil-katilabilirsiniz' },
  { name: 'İLETİŞİM', href: '/iletisim' },
];

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [forceExpand, setForceExpand] = useState(false);
  const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [hovered, setHovered] = useState<string | null>(null);
  const [isAppModalOpen, setIsAppModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isScrolled = currentScrollY > 60;

      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
        if (isScrolled) setForceExpand(false);
      }

      // Auto-collapse if user scrolls a decent amount after expanding the pill
      if (forceExpand && Math.abs(currentScrollY - lastScrollY) > 60) {
        setForceExpand(false);
      }
      lastScrollY = currentScrollY;
    };

    const handleResize = () => setIsMobile(window.innerWidth < 1024);

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [scrolled, forceExpand]);

  useEffect(() => {
    setIsMobilePanelOpen(false);
    setForceExpand(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu open
  useEffect(() => {
    if (isMobilePanelOpen) {
      document.body.style.overflow = 'hidden';
    } else if (!isAppModalOpen) {
      document.body.style.overflow = '';
    }
  }, [isMobilePanelOpen, isAppModalOpen]);

  const navState = !scrolled ? 'top' : forceExpand ? 'expanded' : 'collapsed';

  const containerVariants = {
    top: {
      width: '100%',
      maxWidth: '100%',
      height: '64px',
      borderRadius: '0px',
      y: 0,
      background: 'rgba(255, 255, 255, 0.95)',
      boxShadow: '0 1px 0 0 rgba(230, 81, 0, 0.1), 0 4px 12px rgba(0,0,0,0.03)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      overflow: 'visible'
    },
    collapsed: {
      width: isMobile ? '64px' : '48px',
      maxWidth: isMobile ? '64px' : '48px',
      height: isMobile ? '64px' : '48px',
      borderRadius: isMobile ? '32px' : '24px',
      y: 20,
      background: isMobile ? '#0f2d5e' : 'linear-gradient(135deg, #e65100 0%, #ff9800 100%)',
      boxShadow: isMobile ? '0 12px 32px rgba(15,45,94,0.4), inset 0 0 12px rgba(255,255,255,0.1)' : '0 12px 32px rgba(230,81,0,0.4), inset 0 0 12px rgba(255,255,255,0.3)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      overflow: 'hidden'
    },
    expanded: {
      width: '95%',
      maxWidth: '1100px',
      height: '62px',
      borderRadius: '40px',
      y: 16,
      background: 'rgba(255, 255, 255, 0.96)',
      boxShadow: '0 20px 40px rgba(0,0,0,0.1), 0 0 0 1px rgba(230, 81, 0, 0.1)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      overflow: 'visible'
    }
  };

  return (
    <>
      <style>{`
        .nav-hamburger button svg { color: #0f2d5e !important; stroke: #0f2d5e !important; }
        .nav-x-icon { color: #0f2d5e !important; stroke: #0f2d5e !important; }
        @media (max-width: 767px) {
          .nav-desktop { display: none !important; }
          .nav-cta-desktop { display: none !important; }
          .nav-hamburger { display: flex !important; align-items: center; justify-content: center; }
        }
        @media (min-width: 768px) {
          .nav-hamburger { display: none !important; }
        }
        .nav-link-underline {
          position: absolute;
          bottom: 4px;
          left: 14px;
          right: 14px;
          height: 2px;
          border-radius: 2px;
        }
        .nav-cta {
          text-decoration: none;
          padding: ${navState === 'top' ? '10px 24px' : '8px 18px'};
          background: linear-gradient(135deg, #e65100 0%, #ff9800 100%);
          color: #fff;
          font-size: ${navState === 'top' ? '11px' : '10px'};
          font-weight: 900;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          font-family: var(--font-headline);
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          display: flex;
          align-items: center;
          gap: 8px;
          border-radius: ${navState === 'top' ? '12px' : '30px'};
          position: relative;
          overflow: hidden;
          box-shadow: 0 10px 25px rgba(230, 81, 0, 0.3);
        }
        .nav-cta::before {
          content: '';
          position: absolute;
          top: 0; left: -100%;
          width: 50%; height: 100%;
          background: linear-gradient(to right, transparent, rgba(255,255,255,0.3), transparent);
          transform: skewX(-25deg);
          transition: none;
        }
        .nav-cta:hover {
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 15px 35px rgba(230, 81, 0, 0.4), 0 0 15px rgba(230, 81, 0, 0.2);
          background: linear-gradient(135deg, #ff9800 0%, #e65100 100%);
        }
        .nav-cta:hover::before {
          animation: shine 0.75s infinite;
        }
        @keyframes shine {
          100% { left: 200%; }
        }
        .nav-link-hover-line {
          position: absolute;
          bottom: 4px;
          left: 14px;
          right: 14px;
          height: 1px;
          background: rgba(255,255,255,0.15);
          border-radius: 2px;
        }
        .animate-nav-wrapper {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          pointer-events: none;
        }
        .nav-bg-animation {
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          opacity: 0.25;
        }
        .nav-blob {
          position: absolute;
          width: 320px;
          height: 320px;
          border-radius: 50%;
          filter: blur(60px);
          background: #0ea5e9;
        }
        .nav-blob-1 {
          top: -100px; left: -50px;
          animation: nav-float-1 15s infinite alternate ease-in-out;
        }
        .nav-blob-2 {
          bottom: -100px; right: -50px;
          background: #38bdf8;
          animation: nav-float-2 18s infinite alternate ease-in-out;
        }
        @keyframes nav-float-1 {
          0% { transform: translate(0, 0) scale(1); }
          100% { transform: translate(100px, 40px) scale(1.2); }
        }
        @keyframes nav-float-2 {
          0% { transform: translate(0, 0) scale(1); }
          100% { transform: translate(-80px, -30px) scale(1.1); }
        }
      `}</style>

      {/* Floating Header Container */}
      <div className="animate-nav-wrapper">
        <motion.nav
          key="navbar-force-remount"
          initial={false}
          animate={navState}
          variants={containerVariants}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          style={{
            pointerEvents: 'auto',
            overflow: 'visible',
            position: 'relative'
          }}
        >
          <div className="nav-bg-animation">
            <div className="nav-blob nav-blob-1" />
            <div className="nav-blob nav-blob-2" />
          </div>
          {scrolled && navState === 'top' && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 1,
              background: 'linear-gradient(90deg, transparent, rgba(230, 81, 0, 0.5), transparent)',
            }} />
          )}

          <AnimatePresence mode="wait" initial={false}>
            {navState === 'collapsed' ? (
              <motion.button
                key="collapsed-btn"
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.6 }}
                transition={{ duration: 0.2 }}
                onClick={() => setForceExpand(true)}
                style={{
                  width: '100%', height: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'none', border: 'none', color: '#fff', cursor: 'pointer',
                  padding: 0
                }}
                aria-label="Expand menu"
              >
                <Menu size={isMobile ? 24 : 20} />
              </motion.button>
            ) : (
              <motion.div
                key="expanded-content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'relative',
                  width: '100%', height: '100%',
                  display: 'flex', alignItems: 'center',
                  justifyContent: navState === 'expanded' ? 'space-between' : 'space-between',
                  padding: navState === 'expanded' ? '0 1.25rem' : '0 2rem',
                  maxWidth: navState === 'expanded' ? '100%' : '1280px',
                  margin: '0 auto',
                  boxSizing: 'border-box',
                  gap: navState === 'expanded' ? '0.5rem' : '0'
                }}
              >
                {/* Logo */}
                <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ textDecoration: 'none' }}>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 8 }}>
                    <img src={logoImg} alt="FlyingSlap Logo" loading="eager" fetchPriority="high" decoding="sync" style={{
                      width: isMobile ? 38 : (navState === 'expanded' ? 36 : 42),
                      height: isMobile ? 38 : (navState === 'expanded' ? 36 : 42),
                      objectFit: 'contain',
                      imageRendering: 'auto'
                    }} />
                    <span style={{ 
                      fontSize: isMobile ? 18 : (navState === 'expanded' ? 17 : 19), 
                      fontWeight: 900, 
                      letterSpacing: '-0.04em', 
                      textTransform: 'uppercase', 
                      color: '#0f2d5e', 
                      fontFamily: "var(--font-headline)", 
                      lineHeight: 1 
                    }}>
                      <span style={{ color: '#0f2d5e' }}>FLYING</span><span style={{ color: '#e65100' }}>SLAP</span>
                    </span>
                  </motion.div>
                </Link>

                {/* Desktop Nav */}
                <div className="nav-desktop" style={{
                  position: navState === 'expanded' ? 'relative' : 'absolute',
                  left: navState === 'expanded' ? 'auto' : '50%',
                  transform: navState === 'expanded' ? 'none' : 'translateX(-50%)',
                  flex: navState === 'expanded' ? '1' : undefined,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: navState === 'expanded' ? 2 : 8,
                }}>
                  {navLinks.map((link) => {
                    const isActive = link.href ? location.pathname === link.href : (link.subLinks && link.subLinks.some(sub => location.pathname === sub.href));
                    const isHov = hovered === link.name;
                    return (
                      <motion.div key={link.name} whileTap={{ scale: 0.95 }}
                          onMouseEnter={() => setHovered(link.name)}
                          onMouseLeave={() => setHovered(null)}
                          style={{ position: 'relative' }}
                      >
                        {link.href ? (
                          <Link
                            to={link.href}
                            style={{
                              position: 'relative',
                              textDecoration: 'none',
                              padding: navState === 'expanded' ? '6px 10px' : '8px 16px',
                              fontSize: navState === 'expanded' ? 11 : 12,
                              fontWeight: 800,
                              letterSpacing: '0.12em',
                              fontFamily: "'Inter', 'Outfit', sans-serif",
                              color: isActive ? '#fff' : (isHov ? '#e65100' : '#0f2d5e'),
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 2
                            }}
                          >
                            <span style={{ position: 'relative', zIndex: 10, whiteSpace: 'nowrap' }}>{link.name}</span>
  
                            {isActive && (
                              <motion.div
                                layoutId="nav-pill"
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  background: 'linear-gradient(135deg, #e65100 0%, #ff9800 100%)',
                                  borderRadius: '12px',
                                  zIndex: 1,
                                  boxShadow: '0 8px 20px rgba(230, 81, 0, 0.3)'
                                }}
                                transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                              />
                            )}
  
                            {!isActive && isHov && (
                              <motion.div
                                layoutId="nav-hover-pill"
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  background: 'rgba(230, 81, 0, 0.08)',
                                  borderRadius: '12px',
                                  zIndex: 1
                                }}
                                transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                              />
                            )}
                          </Link>
                        ) : (
                          <div
                            style={{
                              position: 'relative',
                              textDecoration: 'none',
                              padding: navState === 'expanded' ? '6px 10px' : '8px 16px',
                              fontSize: navState === 'expanded' ? 11 : 12,
                              fontWeight: 800,
                              letterSpacing: '0.12em',
                              fontFamily: "'Inter', 'Outfit', sans-serif",
                              color: isActive ? '#fff' : (isHov ? '#e65100' : '#0f2d5e'),
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 2,
                              cursor: 'pointer'
                            }}
                          >
                            <span style={{ position: 'relative', zIndex: 10, whiteSpace: 'nowrap' }}>{link.name}</span>
  
                            {isActive && (
                              <motion.div
                                layoutId="nav-pill"
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  background: 'linear-gradient(135deg, #e65100 0%, #ff9800 100%)',
                                  borderRadius: '12px',
                                  zIndex: 1,
                                  boxShadow: '0 8px 20px rgba(230, 81, 0, 0.3)'
                                }}
                                transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                              />
                            )}
  
                            {!isActive && isHov && (
                              <motion.div
                                layoutId="nav-hover-pill"
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  background: 'rgba(230, 81, 0, 0.08)',
                                  borderRadius: '12px',
                                  zIndex: 1
                                }}
                                transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                              />
                            )}
                          </div>
                        )}

                        {link.subLinks && isHov && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.2 }}
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              paddingTop: '12px', // Invisible bridge to prevent mouse-leave
                              zIndex: 100
                            }}
                          >
                            <div style={{
                              background: 'rgba(255, 255, 255, 0.98)',
                              backdropFilter: 'blur(10px)',
                              borderRadius: '16px',
                              boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(230, 81, 0, 0.1)',
                              padding: '10px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                              minWidth: '220px',
                            }}>
                              {link.subLinks.map(sub => (
                                <Link
                                  key={sub.name}
                                  to={sub.href}
                                  onClick={() => setHovered(null)}
                                  style={{
                                    textDecoration: 'none',
                                    color: location.pathname === sub.href ? '#e65100' : '#0f2d5e',
                                    padding: '12px 18px',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    letterSpacing: '0.08em',
                                    fontFamily: "'Inter', 'Outfit', sans-serif",
                                    borderRadius: '10px',
                                    transition: 'all 0.2s',
                                    background: location.pathname === sub.href ? 'rgba(230, 81, 0, 0.05)' : 'transparent',
                                    whiteSpace: 'nowrap'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(230, 81, 0, 0.08)';
                                    e.currentTarget.style.color = '#e65100';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = location.pathname === sub.href ? 'rgba(230, 81, 0, 0.05)' : 'transparent';
                                    e.currentTarget.style.color = location.pathname === sub.href ? '#e65100' : '#0f2d5e';
                                  }}
                                >
                                  {sub.name}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>

                {/* Desktop CTA & Close Button on Pill */}
                <div className="nav-desktop" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.25rem',
                  paddingRight: navState === 'top' ? '3rem' : '0'
                }}>
                  {/* CTA — always shown, compact in expanded state */}
                  <div
                    className="nav-cta"
                    onClick={() => setIsAppModalOpen(true)}
                    style={{
                      cursor: 'pointer',
                      padding: navState === 'expanded' ? '7px 14px' : undefined,
                      fontSize: navState === 'expanded' ? '10px' : undefined,
                      borderRadius: navState === 'expanded' ? '20px' : undefined,
                    }}
                  >
                    <span>BİZE KATIL</span>
                    <ChevronRight size={navState === 'expanded' ? 13 : 16} strokeWidth={3} />
                  </div>

                  {navState === 'expanded' && (
                    <button
                      onClick={() => setForceExpand(false)}
                      style={{
                        background: 'rgba(0,0,0,0.05)',
                        border: 'none',
                        color: '#0f2d5e',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = '#e65100';
                        e.currentTarget.style.color = '#fff';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = 'rgba(0,0,0,0.05)';
                        e.currentTarget.style.color = '#0f2d5e';
                      }}
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>

                {/* Mobile Hamburger Inside Pill/TopNav */}
                <div className="nav-hamburger" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0f2d5e', padding: 6, lineHeight: 0 }}
                    onClick={() => setIsMobilePanelOpen(true)}
                    aria-label="Open side menu"
                  >
                    <Menu size={24} />
                  </button>

                  {navState === 'expanded' && (
                    <button
                      onClick={() => setForceExpand(false)}
                      style={{ background: 'none', border: 'none', color: '#0f2d5e', cursor: 'pointer', padding: 6, lineHeight: 0 }}
                    >
                      <X size={24} color="#0f2d5e" className="nav-x-icon" />
                    </button>
                  )}
                </div>

              </motion.div>
            )}
          </AnimatePresence>
        </motion.nav>
      </div>

      {/* Mobile Side Panel Overlay */}
      <AnimatePresence>
        {isMobilePanelOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setIsMobilePanelOpen(false)}
              style={{
                position: 'fixed', inset: 0, zIndex: 110,
                background: 'rgba(15, 45, 94, 0.4)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
              }}
            />

            {/* Side Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              style={{
                position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 115,
                width: 300,
                background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderLeft: '1px solid rgba(255, 255, 255, 0.5)',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '-10px 0 40px rgba(0, 0, 0, 0.4)',
                overflow: 'hidden'
              }}
            >
              {/* HUD Accents Removed */}
              
              {/* Scanline Animation */}
              <motion.div
                animate={{ y: ['-100%', '1000%'] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                style={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
                  background: 'linear-gradient(to right, transparent, rgba(15, 45, 94, 0.15), transparent)',
                  zIndex: 1
                }}
              />

              {/* Panel Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 1.5rem', height: 84, borderBottom: '1px solid rgba(255, 255, 255, 0.1)', position: 'relative', zIndex: 10 }}>
                <button 
                  onClick={() => setIsMobilePanelOpen(false)} 
                  style={{ 
                    background: 'rgba(15, 45, 94, 0.05)', border: 'none', cursor: 'pointer', color: '#0f2d5e', 
                    width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Navigation Links */}
              <div style={{ flex: 1, padding: '2rem 1.25rem', display: 'flex', flexDirection: 'column', gap: 10, position: 'relative', zIndex: 10 }}>
                {navLinks.map((link, i) => {
                  if (link.subLinks) {
                    return (
                      <motion.div 
                        key={link.name} 
                        initial={{ opacity: 0, x: 20 }} 
                        animate={{ opacity: 1, x: 0 }} 
                        transition={{ delay: 0.1 + i * 0.1 }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            padding: '16px 20px',
                            borderRadius: '16px',
                            background: 'rgba(255, 255, 255, 0.3)',
                            border: '1px solid rgba(255, 255, 255, 0.4)',
                          }}
                        >
                          <div style={{
                            fontSize: 15,
                            fontWeight: 800,
                            letterSpacing: '0.12em',
                            fontFamily: "var(--font-headline)",
                            color: '#0f2d5e',
                            marginBottom: '4px'
                          }}>
                            {link.name}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '12px', borderLeft: '2px solid rgba(15, 45, 94, 0.1)' }}>
                            {link.subLinks.map(sub => {
                              const isSubActive = location.pathname === sub.href;
                              return (
                                <Link 
                                  key={sub.name}
                                  to={sub.href}
                                  onClick={() => setIsMobilePanelOpen(false)}
                                  style={{
                                    textDecoration: 'none',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    letterSpacing: '0.08em',
                                    fontFamily: "var(--font-headline)",
                                    color: isSubActive ? '#e65100' : '#0f2d5e',
                                    padding: '8px 0',
                                    transition: 'color 0.2s'
                                  }}
                                >
                                  {sub.name}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      </motion.div>
                    );
                  }

                  const isActive = location.pathname === link.href;
                  return (
                    <motion.div 
                      key={link.name} 
                      initial={{ opacity: 0, x: 20 }} 
                      animate={{ opacity: 1, x: 0 }} 
                      transition={{ delay: 0.1 + i * 0.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Link 
                        to={link.href!}
                        onClick={() => setIsMobilePanelOpen(false)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '16px 20px',
                          textDecoration: 'none',
                          borderRadius: '16px',
                          fontSize: 15,
                          fontWeight: 800,
                          letterSpacing: '0.12em',
                          fontFamily: "var(--font-headline)",
                          color: '#0f2d5e',
                          background: isActive ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.3)',
                          border: '1px solid',
                          borderColor: isActive ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.4)',
                          transition: 'all 0.3s ease',
                          boxShadow: isActive ? '0 10px 25px rgba(15, 45, 94, 0.05)' : 'none'
                        }}
                      >
                        {link.name}
                        <ChevronRight size={16} style={{ opacity: isActive ? 1 : 0.4, color: '#0f2d5e' }} />
                      </Link>
                    </motion.div>
                  );
                })}

                  <div style={{ marginTop: 'auto', paddingTop: '2rem' }}>
                    <div
                      onClick={() => { setIsMobilePanelOpen(false); setIsAppModalOpen(true); }}
                      style={{
                        background: 'linear-gradient(135deg, #e65100 0%, #ff9800 100%)',
                        color: '#fff',
                        padding: '18px',
                        borderRadius: '16px',
                        textAlign: 'center',
                        fontWeight: 900,
                        fontSize: 14,
                        letterSpacing: '0.15em',
                        boxShadow: '0 15px 35px rgba(230, 81, 0, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}>
                      BİZE KATIL <Send size={16} />
                    </div>
                  </div>
              </div>

              {/* Panel Footer Area */}
              <div style={{ padding: '1.5rem', position: 'relative', zIndex: 10 }}>
                {/* Clean footer area without text */}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Application Modal */}
      <ApplicationModal isOpen={isAppModalOpen} onClose={() => setIsAppModalOpen(false)} />
    </>
  );
};

export default Navbar;
